import { GetOrg } from "@/app/api/org/getOrg";
import { AgencyDirectoryContact } from "@/types/agencies";
import { TOKEN } from "@/types/common/ids";
import { Org } from "@/types/org";
import { apiFetch, apiStatus } from "@/lib/api";
import { GAIL_API } from "./common/constants/routes";
import { GetCountryFromLocale } from "./common/generic";
import { GetPhoneNumberForDb } from "./common/phone";

const trimOrUndefined = (value?: string): string | undefined =>
  value === undefined ? undefined : value.trim();

type DirectoryContactRequest = Omit<AgencyDirectoryContact, "id" | "phoneNumbers"> & {
  phoneNumbers: Array<Omit<AgencyDirectoryContact["phoneNumbers"][number], "id">>;
};

export const NormalizeDirectoryContact = (
  contact: AgencyDirectoryContact
): AgencyDirectoryContact => ({
  ...contact,
  firstName: trimOrUndefined(contact.firstName),
  lastName: trimOrUndefined(contact.lastName),
  nickname: trimOrUndefined(contact.nickname),
  firstNamePronunciation: trimOrUndefined(contact.firstNamePronunciation),
  lastNamePronunciation: trimOrUndefined(contact.lastNamePronunciation),
  email: trimOrUndefined(contact.email),
  department: trimOrUndefined(contact.department),
  phoneNumbers: contact.phoneNumbers.map((pn) => ({
    ...pn,
    extension: trimOrUndefined(pn.extension),
  })),
});

export const BuildDirectoryContactRequest = (
  contact: AgencyDirectoryContact
): DirectoryContactRequest => {
  const { id: _id, phoneNumbers, ...normalizedContact } = NormalizeDirectoryContact(contact);

  return {
    ...normalizedContact,
    phoneNumbers: phoneNumbers.map(({ id: _phoneNumberId, ...phoneNumber }) => phoneNumber),
  };
};

export const GetDirectory = async (
  token?: TOKEN
): Promise<AgencyDirectoryContact[] | null> => {
  const contacts = await apiFetch<AgencyDirectoryContact[]>(
    `${GAIL_API}/.internal/org_directory`,
    "GET",
    {
      accessToken: token,
    }
  );

  if (!contacts) {
    return null;
  }

  return contacts.sort((a, b) => {
    const aName =
      a.type === "individual"
        ? (a.firstName || "").toLowerCase()
        : (a.department || "").toLowerCase();
    const bName =
      b.type === "individual"
        ? (b.firstName || "").toLowerCase()
        : (b.department || "").toLowerCase();

    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  });
};

/** Thrown by {@link SaveDirectoryContacts} when a write fails or the refreshed list can't load. */
export class DirectoryContactSaveError extends Error {
  constructor(message = "Directory contact save failed") {
    super(message);
    this.name = "DirectoryContactSaveError";
  }
}

/**
 * Upserts contacts with a rolling window of 7 concurrent requests. Returns
 * the number of failed writes, or `null` when no org could be resolved.
 */
const writeDirectoryContacts = async (
  contacts: AgencyDirectoryContact[],
  org: Org | null,
  token?: TOKEN
): Promise<{ failed: number } | null> => {
  let orgToUse = org;

  if (!orgToUse && token) {
    orgToUse = await GetOrg(token);
  }

  if (!orgToUse) {
    return null;
  }

  const country = GetCountryFromLocale(orgToUse.localization);

  const maxConcurrent = 7;
  const inProgress = new Set<Promise<void>>();
  let nextContactIndex = 0;
  let failed = 0;

  while (nextContactIndex < contacts.length || inProgress.size > 0) {
    while (inProgress.size < maxConcurrent && nextContactIndex < contacts.length) {
      const contact = contacts[nextContactIndex];
      nextContactIndex++;

      const contactPromise = (async () => {
        let contactToSave = NormalizeDirectoryContact(contact);

        contactToSave.phoneNumbers = contactToSave.phoneNumbers
          .map((pn) => ({
            phone: GetPhoneNumberForDb(pn.phone, country) ?? pn.phone,
            type: pn.type,
            id: pn.id,
            extension: pn.extension,
          }))
          .filter((pn) => pn.phone !== "");
        const contactRequest = BuildDirectoryContactRequest(contactToSave);

        const url = contactToSave.id
          ? `${GAIL_API}/.internal/org_directory/${contactToSave.id}`
          : `${GAIL_API}/.internal/org_directory`;
        const method = contactToSave.id ? "PUT" : "POST";

        const result = await apiStatus(url, method, {
          body: contactRequest,
          ...(token && { accessToken: token }),
        });
        if (!result.success) {
          failed++;
        }
      })();

      inProgress.add(contactPromise);

      contactPromise.finally(() => {
        inProgress.delete(contactPromise);
      });
    }

    if (inProgress.size > 0) {
      await Promise.race(Array.from(inProgress));
    }
  }

  return { failed };
};

/** Best-effort bulk upsert: individual write failures are ignored and the refreshed list is returned. */
export const AddDirectoryContacts = async (
  contacts: AgencyDirectoryContact[],
  org: Org | null,
  token?: TOKEN
): Promise<AgencyDirectoryContact[] | null> => {
  if (!contacts || contacts.length === 0) {
    return null;
  }

  const result = await writeDirectoryContacts(contacts, org, token);
  if (!result) {
    return null;
  }

  return await GetDirectory(token);
};

/**
 * Strict upsert for flows that must confirm persistence (e.g. the contact
 * modal). Throws {@link DirectoryContactSaveError} if any write fails.
 */
export const SaveDirectoryContacts = async (
  contacts: AgencyDirectoryContact[],
  org: Org | null
): Promise<AgencyDirectoryContact[]> => {
  const result = await writeDirectoryContacts(contacts, org);
  if (!result || result.failed > 0) {
    throw new DirectoryContactSaveError();
  }

  const refreshed = await GetDirectory();
  if (!refreshed) {
    throw new DirectoryContactSaveError("Directory contact saved but the directory could not be reloaded");
  }

  return refreshed;
};

export const RemoveDirectoryContact = async (
  contactId: string,
  token?: TOKEN
): Promise<AgencyDirectoryContact[] | null> => {
  const response = await apiFetch<boolean>(`${GAIL_API}/.internal/org_directory/${contactId}`, "DELETE", { body: null, doNotParse: true, fallback: true, silent: false, alwaysReturn: true, accessToken: token });

  if (response) {
    console.error("Directory Contact Failed to Removed");
  }

  const responseGet = await GetDirectory(token);

  if (!responseGet) {
    return null;
  }

  return responseGet;
};
